import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { receiptStorageSlice } from 'app-components/flux/storage/receiptStorageSlice';

import { useAppDispatch, useAppStore } from 'hooks/redux';
import { getLocalReceiptPreviewUrl } from 'utils/receiptLocalPreview';

import ReceiptImageFallback from './ReceiptImageFallback';
import { getReceiptImageUrl, isPdfMimeType } from './receiptImageUrl';

const receiptImageUrlCache = new Map<string, string>();

const isImageMimeType = (mimeType?: string): boolean => mimeType?.toLowerCase().startsWith('image/') === true;

// Every row without a synced URL used to dispatch its own full storage fetch on mount,
// flooding the API when a list first renders. Share a single in-flight fetch instead.
type StorageDispatch = ReturnType<typeof useAppDispatch>;
let pendingStorageFetch: Promise<unknown> | null = null;
const fetchStorageDocumentsShared = (dispatch: StorageDispatch): Promise<unknown> => {
  if (!pendingStorageFetch) {
    pendingStorageFetch = dispatch(receiptStorageSlice.actions.fetch())
      .unwrap()
      .finally(() => {
        pendingStorageFetch = null;
      });
  }

  return pendingStorageFetch;
};

// The same document can be mounted in several views at once (list row, details dialog);
// share one in-flight signed-URL request per document.
const pendingNoCdnUrlSyncs = new Map<string, Promise<string | undefined>>();
const syncNoCdnUrlShared = (dispatch: StorageDispatch, documentId: string): Promise<string | undefined> => {
  let pending = pendingNoCdnUrlSyncs.get(documentId);
  if (!pending) {
    pending = dispatch(receiptStorageSlice.actions.syncNoCdnUrl(documentId))
      .unwrap()
      .then((result) => result?.url)
      .finally(() => {
        pendingNoCdnUrlSyncs.delete(documentId);
      });
    pendingNoCdnUrlSyncs.set(documentId, pending);
  }

  return pending;
};

type Props = {
  documentId: string;
  alt: string;
  localFileUri?: string;
  mimeType?: string;
  width?: number | string;
  height?: number | string;
  objectFit?: 'contain' | 'cover';
  borderRadius?: number;
  preserveLocalPreview?: boolean;
  fallbackTone?: 'default' | 'error';
};

const ReceiptImage = ({
  documentId,
  alt,
  localFileUri,
  mimeType,
  width = 36,
  height = 36,
  objectFit = 'cover',
  borderRadius = 1,
  preserveLocalPreview = false,
  fallbackTone = 'default',
}: Props) => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const localPreviewUrl =
    (localFileUri ? getLocalReceiptPreviewUrl(localFileUri) : undefined) ??
    (preserveLocalPreview ? getLocalReceiptPreviewUrl(documentId) : undefined);
  const localImageUrl = localPreviewUrl ?? localFileUri;
  const cachedImageUrl = localFileUri ? undefined : receiptImageUrlCache.get(documentId);
  const requestIdRef = useRef(0);
  const displayedLocalImageUrlRef = useRef<string | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>(cachedImageUrl);
  const [displayedImageUrl, setDisplayedImageUrl] = useState<string | undefined>(cachedImageUrl);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(!cachedImageUrl);
  const [hasRetried, setHasRetried] = useState(false);

  const showFallback = useCallback((): void => {
    displayedLocalImageUrlRef.current = undefined;
    setDisplayedImageUrl(undefined);
    setHasError(true);
    setIsLoading(false);
  }, []);

  const syncImageUrl = useCallback(
    async (forceSync = false): Promise<void> => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setIsLoading(true);
      setHasError(false);

      try {
        let storageDocument = receiptStorageSlice.selectors.selectById(store.getState(), documentId);
        let isPdfDocument = isPdfMimeType(mimeType) || isPdfMimeType(storageDocument?.mimeType);
        if (localPreviewUrl || (localFileUri && !isPdfDocument)) {
          setImageUrl(localImageUrl);
          return;
        }

        // The document list endpoint never returns signed urls, so fetching it only helps
        // when we still need to learn the mime type (PDF vs image). A known image can go
        // straight to the signed-URL request.
        const isKnownImage = isImageMimeType(mimeType) || isImageMimeType(storageDocument?.mimeType);

        let url = forceSync ? undefined : getReceiptImageUrl(storageDocument, mimeType);
        if (!url && isPdfDocument) {
          storageDocument = await dispatch(receiptStorageSlice.actions.syncDocument(documentId)).unwrap();
          url = getReceiptImageUrl(storageDocument, mimeType);
        } else if (!url && !forceSync && !isKnownImage) {
          await fetchStorageDocumentsShared(dispatch);
          storageDocument = receiptStorageSlice.selectors.selectById(store.getState(), documentId);
          url = getReceiptImageUrl(storageDocument, mimeType);
          isPdfDocument = isPdfMimeType(storageDocument?.mimeType);
          if (!url && isPdfDocument) {
            storageDocument = await dispatch(receiptStorageSlice.actions.syncDocument(documentId)).unwrap();
            url = getReceiptImageUrl(storageDocument, mimeType);
          }
        }

        if (!url && !isPdfDocument) {
          url = await syncNoCdnUrlShared(dispatch, documentId);
        }

        if (!url) {
          throw new Error('Receipt image URL sync did not return a URL.');
        }

        if (requestIdRef.current === requestId) {
          setImageUrl(url);
        }
      } catch {
        if (requestIdRef.current === requestId) {
          if (displayedLocalImageUrlRef.current) {
            setImageUrl(undefined);
            setHasError(false);
            setIsLoading(false);
          } else {
            showFallback();
          }
        }
      }
    },
    [dispatch, documentId, localFileUri, localImageUrl, localPreviewUrl, mimeType, showFallback, store],
  );

  useEffect(() => {
    const cachedUrl = localFileUri ? undefined : receiptImageUrlCache.get(documentId);
    if (cachedUrl) {
      displayedLocalImageUrlRef.current = undefined;
      setImageUrl(cachedUrl);
      setDisplayedImageUrl(cachedUrl);
      setHasError(false);
      setIsLoading(false);
      setHasRetried(false);

      return () => {
        requestIdRef.current += 1;
      };
    }

    setImageUrl(undefined);
    setHasRetried(false);
    if (localImageUrl || !displayedLocalImageUrlRef.current) {
      displayedLocalImageUrlRef.current = undefined;
      setDisplayedImageUrl(undefined);
    }
    void syncImageUrl();

    return () => {
      requestIdRef.current += 1;
    };
  }, [documentId, localFileUri, localImageUrl, syncImageUrl]);

  const handleError = (): void => {
    // A local blob only exists while the receipt is a local pending record, so its
    // documentId is still a placeholder — there is no remote image to retry against.
    if (!localFileUri && !hasRetried) {
      receiptImageUrlCache.delete(documentId);
      if (displayedImageUrl === imageUrl) {
        setDisplayedImageUrl(undefined);
      }
      setHasRetried(true);
      void syncImageUrl(true);
      return;
    }

    showFallback();
  };

  const handleLoad = (): void => {
    if (!imageUrl) {
      return;
    }

    // Local previews are owned (and eventually revoked) by the preview registry, so only
    // remote URLs belong in the shared cache.
    if (!localFileUri && imageUrl !== localPreviewUrl) {
      receiptImageUrlCache.set(documentId, imageUrl);
    }
    displayedLocalImageUrlRef.current = localImageUrl === imageUrl ? imageUrl : undefined;
    setDisplayedImageUrl(imageUrl);
    setIsLoading(false);
    setHasRetried(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        borderRadius,
        bgcolor: hasError && fallbackTone === 'error' ? 'error.light' : 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
      data-testid={`receipt-image-${documentId}`}
    >
      {isLoading && !hasError && !displayedImageUrl ? (
        <Skeleton variant='rounded' width='100%' height='100%' data-testid={`receipt-image-${documentId}-loading`} />
      ) : null}
      {displayedImageUrl && !hasError ? (
        <Box
          component='img'
          src={displayedImageUrl}
          alt={alt}
          onError={handleError}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit,
          }}
        />
      ) : null}
      {imageUrl && imageUrl !== displayedImageUrl && !hasError ? (
        <Box
          component='img'
          src={imageUrl}
          alt={displayedImageUrl ? '' : alt}
          aria-hidden={displayedImageUrl ? true : undefined}
          onLoad={handleLoad}
          onError={handleError}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit,
            opacity: 0,
          }}
        />
      ) : null}
      {hasError ? <ReceiptImageFallback testId={`receipt-image-${documentId}-fallback`} tone={fallbackTone} /> : null}
    </Box>
  );
};

export default ReceiptImage;
